import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  QueryStateBoundary,
} from "@/components/ui";
import {
  useCampaign,
  useCampaignStats,
  usePreviewCampaign,
  useSendCampaign,
} from "@/api/hooks/useCampaigns";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import {
  CAMPAIGN_STATUS_BADGE_VARIANT,
  CAMPAIGN_STATUS_LABELS,
  type CampaignDetail,
  type CampaignSendResult,
} from "@/api/types/campaign";

// Fable scan #18: previewResult.body_html is NOT always server-rendered,
// trusted-origin HTML — when campaign.personalize is true, it's Haiku
// output that embeds the recipient contact's name verbatim into raw HTML
// with no server-side escaping (see api/app/services/campaign_ai.py), and
// contact.name is reachable from the unauthenticated public inquiry form.
// The comment that used to sit on the dangerouslySetInnerHTML below
// ("safe to inject directly") was wrong for that path. No DOMPurify
// dependency exists in this project (web/package.json), and adding one
// for a single call site is more than this fix needs, so this is a small
// self-contained allowlist sanitizer instead: parse the string as HTML,
// walk the tree, drop any element not on the allowlist (and its
// subtree — script/style/iframe/etc never survive), and strip every
// attribute except a small per-purpose allowlist (href/src are further
// restricted to http(s)/mailto so `javascript:`/`data:` URIs and
// `onerror=`-style handlers can never reach the DOM this renders into).
const ALLOWED_TAGS = new Set([
  "A", "B", "STRONG", "I", "EM", "U", "P", "BR", "SPAN", "DIV",
  "UL", "OL", "LI", "H1", "H2", "H3", "H4", "H5", "H6",
  "BLOCKQUOTE", "HR", "TABLE", "THEAD", "TBODY", "TR", "TD", "TH",
  "SMALL", "IMG",
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  A: new Set(["href", "title"]),
  IMG: new Set(["src", "alt", "title"]),
};

function _isSafeUrl(value: string): boolean {
  const trimmed = value.trim().toLowerCase();
  return (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("mailto:") ||
    trimmed.startsWith("/")
  );
}

function _sanitizeNode(node: ChildNode): void {
  // Walk children back-to-front since we may remove/replace nodes.
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove();
      continue;
    }
    const el = child as Element;
    if (!ALLOWED_TAGS.has(el.tagName)) {
      // Drop the whole subtree — this is what keeps <script>,
      // <style>, <iframe>, <object>, <form>, <svg> (onload=), etc. out,
      // rather than unwrapping and leaving their content/attrs behind.
      el.remove();
      continue;
    }
    const allowedForTag = ALLOWED_ATTRS[el.tagName];
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (!allowedForTag || !allowedForTag.has(name)) {
        el.removeAttribute(attr.name);
        continue;
      }
      if ((name === "href" || name === "src") && !_isSafeUrl(attr.value)) {
        el.removeAttribute(attr.name);
      }
    }
    _sanitizeNode(el);
  }
}

function sanitizeCampaignHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  _sanitizeNode(doc.body);
  return doc.body.innerHTML;
}

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: campaign, isLoading, error, refetch } = useCampaign(id);

  const previewCampaign = usePreviewCampaign();
  const sendCampaign = useSendCampaign();

  const [previewResult, setPreviewResult] = useState<{ subject: string; body_html: string } | null>(
    null,
  );
  const [sendResult, setSendResult] = useState<CampaignSendResult | null>(null);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);

  async function handlePreview() {
    if (!campaign) return;
    try {
      const result = await previewCampaign.mutateAsync({ id: campaign.id });
      setPreviewResult(result);
    } catch (err) {
      toastError("Couldn't render preview", getErrorMessage(err));
    }
  }

  async function handleSend() {
    if (!campaign) return;
    try {
      const result = await sendCampaign.mutateAsync(campaign.id);
      setSendResult(result);
      toastSuccess(`Sent to ${result.sent} of ${result.total} recipients`);
    } catch (err) {
      toastError("Couldn't send campaign", getErrorMessage(err));
    } finally {
      setConfirmSendOpen(false);
    }
  }

  const canSend = campaign?.status === "draft";
  const eligibleCount = campaign?.recipients_summary?.count;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6 sm:p-8">
      <Button variant="link" size="sm" onClick={() => navigate("/campaigns")}>
        ← Back to campaigns
      </Button>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {campaign && (
          <>
            <CampaignSummaryCard campaign={campaign} />

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Send</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="rounded-md border border-copper/30 bg-copper/5 px-3 py-2 text-sm text-text-secondary">
                  Recipients are limited to contacts who opted in to email — this is enforced on
                  every send, no exceptions.
                </p>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => void handlePreview()}
                    disabled={previewCampaign.isPending}
                  >
                    {previewCampaign.isPending ? "Rendering..." : "Preview"}
                  </Button>
                  {canSend && (
                    <Button
                      onClick={() => setConfirmSendOpen(true)}
                      disabled={sendCampaign.isPending}
                    >
                      Send campaign
                    </Button>
                  )}
                </div>

                {previewResult && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-text-primary">Email preview</p>
                    <p className="text-xs text-text-muted">Subject: {previewResult.subject}</p>
                    <div
                      data-testid="email-preview-body"
                      className="max-h-96 overflow-auto rounded-md border border-border bg-white p-4 text-sm text-black"
                      // Fable scan #18: previewResult.body_html can be
                      // Haiku-personalized (campaign.personalize=true) and
                      // is NOT escaped server-side in that path, so it is
                      // sanitized here before ever reaching the DOM — see
                      // sanitizeCampaignHtml above.
                      dangerouslySetInnerHTML={{ __html: sanitizeCampaignHtml(previewResult.body_html) }}
                    />
                  </div>
                )}

                {sendResult && (
                  <div
                    data-testid="send-result"
                    className="rounded-md border border-border bg-bg-hover/50 px-3 py-2 text-sm"
                  >
                    Sent {sendResult.sent} · Skipped {sendResult.skipped} · Failed{" "}
                    {sendResult.failed} (of {sendResult.total} eligible)
                  </div>
                )}
              </CardContent>
            </Card>

            <CampaignStatsPanel campaignId={campaign.id} />
          </>
        )}
      </QueryStateBoundary>

      <ConfirmDialog
        open={confirmSendOpen}
        onClose={() => setConfirmSendOpen(false)}
        onConfirm={() => void handleSend()}
        title="Send campaign?"
        message={
          eligibleCount !== undefined
            ? `This will send to ${eligibleCount} eligible recipient${
                eligibleCount === 1 ? "" : "s"
              } who opted in to email. This can't be undone.`
            : "This will send to every eligible recipient who opted in to email. This can't be undone."
        }
        confirmLabel="Send"
        isLoading={sendCampaign.isPending}
      />
    </div>
  );
}

function CampaignSummaryCard({ campaign }: { campaign: CampaignDetail }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{campaign.name}</CardTitle>
        <Badge variant={CAMPAIGN_STATUS_BADGE_VARIANT[campaign.status]} data-testid="campaign-status-badge">
          {CAMPAIGN_STATUS_LABELS[campaign.status]}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Subject</p>
          <p className="text-sm text-text-primary">{campaign.subject}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Body</p>
          <p className="whitespace-pre-wrap text-sm text-text-secondary">
            {campaign.body_template}
          </p>
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-text-muted">
          <span>{campaign.personalize ? "AI personalization on" : "AI personalization off"}</span>
          {campaign.recipients_summary && (
            <span data-testid="recipients-summary-count">
              {campaign.recipients_summary.count} eligible recipient
              {campaign.recipients_summary.count === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Renders a server-computed rate as a CSS bar. `rate` is taken as-is from
 * the API (0-1 fraction, per the `open_rate`/`click_rate` contract fields)
 * — the frontend never recomputes a rate from raw counts, only displays
 * the number the server already produced.
 */
function StatBar({ label, rate }: { label: string; rate: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(rate * 100)));
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-text-secondary">{label}</span>
        <span className="font-medium text-text-primary">{pct}%</span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-bg-muted">
        <div className="h-full rounded-full bg-copper" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CampaignStatsPanel({ campaignId }: { campaignId: string }) {
  const { data: stats, isLoading, error, refetch } = useCampaignStats(campaignId);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Stats</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => void refetch()}>
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
          {stats && (
            <div className="space-y-4" data-testid="campaign-stats">
              <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-text-muted">Total</dt>
                  <dd className="font-semibold text-text-primary">{stats.total}</dd>
                </div>
                <div>
                  <dt className="text-text-muted">Sent</dt>
                  <dd className="font-semibold text-text-primary">{stats.sent}</dd>
                </div>
                <div>
                  <dt className="text-text-muted">Opened</dt>
                  <dd className="font-semibold text-text-primary">{stats.opened}</dd>
                </div>
                <div>
                  <dt className="text-text-muted">Clicked</dt>
                  <dd className="font-semibold text-text-primary">{stats.clicked}</dd>
                </div>
              </dl>

              <StatBar label="Open rate" rate={stats.open_rate} />
              <StatBar label="Click rate" rate={stats.click_rate} />
            </div>
          )}
        </QueryStateBoundary>
      </CardContent>
    </Card>
  );
}
